import React, { useState, useRef, useEffect } from 'react';
import { 
  Bold, Italic, Underline, List, ListOrdered, Link, Image, 
  Code, Quote, Heading1, Heading2, Heading3, AlignLeft, 
  AlignCenter, AlignRight, Eye, EyeOff 
} from 'lucide-react';

const RichTextEditor = ({ value, onChange, placeholder = "Start writing..." }) => {
  const editorRef = useRef(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const formatBlock = (tag) => {
    document.execCommand('formatBlock', false, tag);
    editorRef.current?.focus();
    handleInput();
  };

  const insertHTML = (html) => {
    if (document.queryCommandSupported('insertHTML')) {
      document.execCommand('insertHTML', false, html);
    } else {
      // Fallback for browsers that don't support insertHTML
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const div = document.createElement('div');
        div.innerHTML = html;
        const fragment = document.createDocumentFragment();
        let node;
        while ((node = div.firstChild)) {
          fragment.appendChild(node);
        }
        range.insertNode(fragment);
      }
    }
    editorRef.current?.focus();
    handleInput();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageHtml = `<img src="${e.target.result}" alt="Uploaded image" style="max-width: 100%; height: auto; border-radius: 8px; margin: 16px 0;" />`;
        insertHTML(imageHtml);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLinkInsert = () => {
    const selection = window.getSelection();
    if (selection.toString()) {
      setLinkText(selection.toString());
    }
    setIsLinkModalOpen(true);
  };

  const insertLink = () => {
    if (linkUrl) {
      const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer" style="color: #0f172a; text-decoration: underline;">${linkText || linkUrl}</a>`;
      insertHTML(linkHtml);
    }
    setIsLinkModalOpen(false);
    setLinkUrl('');
    setLinkText('');
  };

  const toolbarButtons = [
    {
      icon: <Heading1 className="w-4 h-4" />,
      title: 'Heading 1',
      action: () => formatBlock('h1')
    },
    {
      icon: <Heading2 className="w-4 h-4" />,
      title: 'Heading 2',
      action: () => formatBlock('h2')
    },
    {
      icon: <Heading3 className="w-4 h-4" />,
      title: 'Heading 3',
      action: () => formatBlock('h3')
    },
    { type: 'separator' },
    {
      icon: <Bold className="w-4 h-4" />,
      title: 'Bold',
      action: () => execCommand('bold')
    },
    {
      icon: <Italic className="w-4 h-4" />,
      title: 'Italic',
      action: () => execCommand('italic')
    },
    {
      icon: <Underline className="w-4 h-4" />,
      title: 'Underline',
      action: () => execCommand('underline')
    },
    { type: 'separator' },
    {
      icon: <List className="w-4 h-4" />,
      title: 'Bullet List',
      action: () => execCommand('insertUnorderedList')
    },
    {
      icon: <ListOrdered className="w-4 h-4" />,
      title: 'Numbered List',
      action: () => execCommand('insertOrderedList')
    },
    { type: 'separator' },
    {
      icon: <AlignLeft className="w-4 h-4" />,
      title: 'Align Left',
      action: () => execCommand('justifyLeft')
    },
    {
      icon: <AlignCenter className="w-4 h-4" />,
      title: 'Align Center',
      action: () => execCommand('justifyCenter')
    },
    {
      icon: <AlignRight className="w-4 h-4" />,
      title: 'Align Right',
      action: () => execCommand('justifyRight')
    },
    { type: 'separator' },
    {
      icon: <Quote className="w-4 h-4" />,
      title: 'Quote',
      action: () => formatBlock('blockquote')
    },
    {
      icon: <Code className="w-4 h-4" />,
      title: 'Code',
      action: () => execCommand('formatBlock', 'pre')
    },
    { type: 'separator' },
    {
      icon: <Link className="w-4 h-4" />,
      title: 'Insert Link',
      action: handleLinkInsert
    },
    {
      icon: <Image className="w-4 h-4" />,
      title: 'Insert Image',
      action: () => document.getElementById('image-upload').click()
    }
  ];

  return (
    <div className="border border-gray-300 rounded-md overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="border-b border-gray-200 bg-gray-50 p-3">
        <div className="flex items-center gap-1 flex-wrap">
          {toolbarButtons.map((button, index) => {
            if (button.type === 'separator') {
              return <div key={index} className="w-px h-6 bg-gray-300 mx-2" />;
            }
            
            return (
              <button
                key={index}
                onClick={button.action}
                className="p-2 hover:bg-gray-200 rounded transition-colors"
                title={button.title}
                type="button"
              >
                {button.icon}
              </button>
            );
          })}
          
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors text-sm"
              type="button"
            >
              {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showPreview ? 'Edit' : 'Preview'}
            </button>
          </div>
        </div>
      </div>

      {/* Editor/Preview Area */}
      <div className="h-96 max-h-96 overflow-y-auto">
        {showPreview ? (
          <div className="p-4 prose prose-slate max-w-none">
            <div 
              dangerouslySetInnerHTML={{ __html: value || '<p>No content yet...</p>' }}
              className="article-preview"
            />
          </div>
        ) : (
          <div
            ref={editorRef}
            contentEditable
            onInput={handleInput}
            className="p-4 h-full outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset prose prose-slate max-w-none"
            style={{ 
              lineHeight: '1.6',
              fontSize: '14px',
              minHeight: '100%'
            }}
            data-placeholder={placeholder}
            suppressContentEditableWarning={true}
          />
        )}
      </div>

      {/* Hidden file input for image uploads */}
      <input
        id="image-upload"
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />

      {/* Link Modal */}
      {isLinkModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Insert Link</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link Text
                </label>
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Link text"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL
                </label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setIsLinkModalOpen(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={insertLink}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  type="button"
                >
                  Insert Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        
        .article-preview h1,
        .article-preview h2,
        .article-preview h3,
        .article-preview h4,
        .article-preview h5,
        .article-preview h6 {
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: #0f172a;
        }
        
        .article-preview h1 { font-size: 2rem; }
        .article-preview h2 { font-size: 1.75rem; }
        .article-preview h3 { font-size: 1.5rem; }
        .article-preview h4 { font-size: 1.25rem; }
        .article-preview h5 { font-size: 1.125rem; }
        .article-preview h6 { font-size: 1rem; }
        
        .article-preview p {
          margin-bottom: 1rem;
          color: #475569;
          line-height: 1.7;
        }
        
        .article-preview ul,
        .article-preview ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }
        
        .article-preview li {
          margin-bottom: 0.25rem;
          color: #475569;
        }
        
        .article-preview blockquote {
          border-left: 4px solid #e2e8f0;
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: #64748b;
          background-color: #f8fafc;
          padding: 1rem;
          border-radius: 0.375rem;
        }
        
        .article-preview pre {
          background-color: #0f172a;
          color: #f8fafc;
          padding: 1rem;
          border-radius: 0.375rem;
          overflow-x: auto;
          margin: 1rem 0;
          font-family: 'Courier New', monospace;
        }
        
        .article-preview code {
          background-color: #f1f5f9;
          padding: 0.125rem 0.25rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
          color: #0f172a;
          font-family: 'Courier New', monospace;
        }
        
        .article-preview a {
          color: #0f172a;
          text-decoration: underline;
        }
        
        .article-preview a:hover {
          color: #475569;
        }
        
        .article-preview img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }
        
        /* Editor styles */
        [contenteditable] h1,
        [contenteditable] h2,
        [contenteditable] h3,
        [contenteditable] h4,
        [contenteditable] h5,
        [contenteditable] h6 {
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: #0f172a;
        }
        
        [contenteditable] h1 { font-size: 2rem; }
        [contenteditable] h2 { font-size: 1.75rem; }
        [contenteditable] h3 { font-size: 1.5rem; }
        [contenteditable] h4 { font-size: 1.25rem; }
        [contenteditable] h5 { font-size: 1.125rem; }
        [contenteditable] h6 { font-size: 1rem; }
        
        [contenteditable] p {
          margin-bottom: 1rem;
          color: #475569;
        }
        
        [contenteditable] ul,
        [contenteditable] ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }
        
        [contenteditable] li {
          margin-bottom: 0.25rem;
          color: #475569;
        }
        
        [contenteditable] blockquote {
          border-left: 4px solid #e2e8f0;
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: #64748b;
          background-color: #f8fafc;
          padding: 1rem;
          border-radius: 0.375rem;
        }
        
        [contenteditable] pre {
          background-color: #0f172a;
          color: #f8fafc;
          padding: 1rem;
          border-radius: 0.375rem;
          overflow-x: auto;
          margin: 1rem 0;
          font-family: 'Courier New', monospace;
        }
        
        [contenteditable] code {
          background-color: #f1f5f9;
          padding: 0.125rem 0.25rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
          color: #0f172a;
          font-family: 'Courier New', monospace;
        }
        
        [contenteditable] a {
          color: #0f172a;
          text-decoration: underline;
        }
        
        [contenteditable] a:hover {
          color: #475569;
        }
        
        [contenteditable] img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;